import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Spraakinvoer voor de bevindingtekst. Typen op een tablet met werkhandschoenen
 * aan is het echte knelpunt op locatie, dus dit is geen extraatje.
 *
 * De Web Speech API is niet overal beschikbaar (en op iOS Safari alleen met
 * netwerk). Is hij er niet, dan meldt de hook dat en blijft typen over - nooit
 * een knop die niets doet.
 */

interface SpeechResultAlternative {
    transcript: string;
}

interface SpeechResult extends ArrayLike<SpeechResultAlternative> {
    isFinal: boolean;
}

interface SpeechResultEvent {
    resultIndex: number;
    results: ArrayLike<SpeechResult>;
}

interface SpeechRecognitionLike {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    start: () => void;
    stop: () => void;
    abort: () => void;
    onresult: ((event: SpeechResultEvent) => void) | null;
    onend: (() => void) | null;
    onerror: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function recognitionConstructor(): SpeechRecognitionConstructor | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const candidate = window as unknown as {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };

    return candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition ?? null;
}

export interface VoiceInput {
    supported: boolean;
    recording: boolean;
    toggle: () => void;
    stop: () => void;
}

export function useVoiceInput(onTranscript: (text: string) => void): VoiceInput {
    const [recording, setRecording] = useState(false);
    const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
    const handlerRef = useRef(onTranscript);
    const [supported] = useState(() => recognitionConstructor() !== null);

    useEffect(() => {
        handlerRef.current = onTranscript;
    }, [onTranscript]);

    useEffect(() => {
        return () => {
            recognitionRef.current?.abort();
            recognitionRef.current = null;
        };
    }, []);

    const stop = useCallback(() => {
        recognitionRef.current?.stop();
        setRecording(false);
    }, []);

    const start = useCallback(() => {
        const Recognition = recognitionConstructor();

        if (!Recognition) {
            return;
        }

        const recognition = new Recognition();
        recognition.lang = 'nl-NL';
        recognition.continuous = true;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            let transcript = '';

            for (let index = event.resultIndex; index < event.results.length; index += 1) {
                const result = event.results[index];

                if (result?.isFinal) {
                    transcript += result[0]?.transcript ?? '';
                }
            }

            if (transcript.trim().length > 0) {
                handlerRef.current(transcript.trim());
            }
        };

        recognition.onend = () => setRecording(false);
        recognition.onerror = () => setRecording(false);

        recognitionRef.current = recognition;
        recognition.start();
        setRecording(true);
    }, []);

    const toggle = useCallback(() => {
        if (recording) {
            stop();

            return;
        }

        start();
    }, [recording, start, stop]);

    return { supported, recording, toggle, stop };
}
